import { Select, Skeleton, Loader } from "@mantine/core";
import type { ProviderViewModel } from "@models/provider";
import type { ModelBasicInfoViewModel, ModelViewModel } from "@models/model";
import { useTranslation } from "@hooks/useTranslation";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { ModelDetailsDisplay } from "./ModelDetailsDisplay";

interface ProviderModelSelectProps {
    isLoadingProviders: boolean;
    isLoadingModels?: boolean;
    providers: ProviderViewModel[];
    models: ModelBasicInfoViewModel[];
    selectedProvider: string;
    selectedModel: ModelViewModel | null;
    onProviderChange: (providerKey: string) => void;
    onModelChange: (modelName: string) => void;
    showModelDetails?: boolean;
}

export const ProviderModelSelect = ({
    isLoadingProviders,
    isLoadingModels,
    providers,
    models,
    selectedProvider,
    selectedModel,
    onProviderChange,
    onModelChange,
    showModelDetails = true,
}: ProviderModelSelectProps) => {
    const { t } = useTranslation();
    const { canCreateDoctype } = useAdminPermissions();

    if (isLoadingProviders) {
        return (
            <>
                <Skeleton height={56} />
                <Skeleton height={56} />
                {showModelDetails && <Skeleton height={120} />}
            </>
        );
    }

    // Read-only mode: user has canEditDoctype but NOT canCreateDoctype.
    // Show the current values without fetching the full provider/model list.
    if (!canCreateDoctype) {
        return (
            <>
                <Select
                    label={t("admin.flowConfigs.labels.provider")}
                    value={selectedProvider || null}
                    data={
                        selectedProvider
                            ? [{ value: selectedProvider, label: selectedProvider }]
                            : []
                    }
                    disabled
                    comboboxProps={{ withinPortal: false }}
                />
                <Select
                    label={t("admin.table.columns.model")}
                    value={selectedModel?.modelName || null}
                    data={
                        selectedModel
                            ? [{
                                  value: selectedModel.modelName,
                                  label: selectedModel.displayName || selectedModel.modelName,
                              }]
                            : []
                    }
                    disabled
                    comboboxProps={{ withinPortal: false }}
                />
                {showModelDetails && selectedModel && (
                    <ModelDetailsDisplay
                        selectedModel={selectedModel}
                        selectedProvider={selectedProvider}
                    />
                )}
            </>
        );
    }

    const providerData = providers.map((provider) => ({
        value: provider.id,
        label: provider.key,
    }));

    const modelData = models.map((model) => ({
        value: model.modelName,
        label: model.displayName || model.modelName,
    }));

    return (
        <>
            <Select
                label={t("admin.flowConfigs.labels.provider")}
                value={selectedProvider || null}
                onChange={(value) => onProviderChange(value || "")}
                data={providerData}
                comboboxProps={{ withinPortal: false }}
            />

            <Select
                label={t("admin.table.columns.model")}
                value={selectedModel?.modelName || null}
                onChange={(value) => onModelChange(value || "")}
                data={modelData}
                disabled={!selectedProvider || isLoadingModels}
                rightSection={isLoadingModels ? <Loader size={16} /> : undefined}
                comboboxProps={{ withinPortal: false }}
            />

            {showModelDetails && selectedModel && (
                <ModelDetailsDisplay
                    selectedModel={selectedModel}
                    selectedProvider={selectedProvider}
                />
            )}
        </>
    );
};
